// @flow
import Budgets from 'containers/Budgets';
import InvestmentHoldings from 'containers/InvestmentHoldings/loadable';
import OverviewPage from 'containers/OverviewPage/loadable';

import { userIsAuthenticatedWithDataset, isAuthenticatedOrElse } from 'utils/auth';
import { isAcme } from 'isAcme';

import Settings from 'containers/Settings/loadable';
import NetIncome from 'containers/NetIncome/loadable';
import SetupPage from 'containers/SetupPage/loadable';
import GettingStartedWebFirst from 'containers/SetupPage/GettingStartedWebFirst';
import Spending from 'containers/Spending/loadable';
import Reports from 'containers/Reports/loadable';
import Transactions from 'containers/Transactions/loadable';
import ScheduledTransactions from 'containers/ScheduledTransactions/loadable';
import SpendingWatchlist from 'containers/SpendingWatchlist/loadable';
import SpendingPlan from 'containers/SpendingPlan/loadable';
import NotFoundPage from 'containers/NotFoundPage/loadable';
import OfflinePage from 'containers/OfflinePage/loadable';
import SetupPageAcme from 'containers/SetupPageAcme/loadable';
import ErrorPage from 'containers/ErrorPage/loadable';
import Investments from 'containers/Investments/loadable';
import Goals from 'containers/Goals/loadable';
import BizMiles from 'containers/BizMiles/loadable';
import GettingStarted from 'containers/GettingStarted/loadable';
import PlatformSelector from 'containers/PlatformSelector/loadable';
import TermsPage from 'containers/Legal/TermsPage/loadable';
import PrivacyPage from "containers/Legal/PrivacyPage/loadable";
import Acknowledge from "containers/Legal/Acknowledgements/loadable"
import FetchSubscriptionPage from 'containers/FetchSubscriptionPage/loadable';
import SharedComponentsPage from 'containers/SharedComponentsPage/loadable';
import UpcomingPage from 'containers/Upcoming/loadable';

import BuildIcon from '@material-ui/icons/Build';
import { qCards } from './QCards/qCardIndex';
import * as navIcons from './navIcons';
import Login from './Login';
import Logout from './Logout';

const ProtectedBudgets = userIsAuthenticatedWithDataset(Budgets);
const ProtectedDashboard = userIsAuthenticatedWithDataset(OverviewPage);
const ProtectedInvestmentHoldings = userIsAuthenticatedWithDataset(InvestmentHoldings);
const ProtectedInvestments = userIsAuthenticatedWithDataset(Investments);
const ProtectedNetIncome = userIsAuthenticatedWithDataset(NetIncome);
const ProtectedScheduledTransactions = userIsAuthenticatedWithDataset(ScheduledTransactions);
const ProtectedSettingsComponent = userIsAuthenticatedWithDataset(Settings);
const ProtectedSpending = userIsAuthenticatedWithDataset(Spending);
const ProtectedReports = userIsAuthenticatedWithDataset(Reports);
const ProtectedTransactions = userIsAuthenticatedWithDataset(Transactions);
const ProtectedSpendingWatchlist = userIsAuthenticatedWithDataset(SpendingWatchlist);
const ProtectedSpendingPlan = userIsAuthenticatedWithDataset(SpendingPlan);
const ProtectedUpcomingPage = userIsAuthenticatedWithDataset(UpcomingPage);
const ProtectedGoals = userIsAuthenticatedWithDataset(Goals);
const ProtectedMiles = userIsAuthenticatedWithDataset(BizMiles);

const GettingStartedPage = isAuthenticatedOrElse(NotFoundPage, GettingStarted);
const PlatformSelectorPage = isAuthenticatedOrElse(PlatformSelector, NotFoundPage);

const navAcmeMainOrder = [
  '', // dashboard
  'transactions',
  'spending-plan',
  'upcoming',
  'watchlist',
  'spending',
  'reports',
  'net-income',
  'investing',
  'goals',
  'miles',
];

const navQuickenMainOrder = [
  '', // dashboard
  'transactions',
  'spending',
  'budgets',
  'net-income',
  'reports',
  'bills-and-income',
  'investments',
];

export const navMainOrder = isAcme ? navAcmeMainOrder : navQuickenMainOrder;

export type FeaturePage = {
  path: string,
  component: any,
  exact: boolean,
  title: string,
  datasetNotNeeded?: boolean,
  authNotNeeded?: boolean,
  nav?: ?'main'|'sub',
  alt?: ?string,
  img?: ?any,
  featureFlag?: ?string,
  id?: ?string,
  open?: ?boolean,
  accountsShowing?: ?boolean,
  exclusiveProject?: ?string,
  hideNav?: ?boolean,
  qCards?: ?any,
  pathParams?: ?Array<string>,
};

/*
 *
 * ADD YOUR FEATURE PAGE HERE
 *
 * Required:
 * path
 * component
 * title        - STRING: title used in the QTip and used for the automation ID
 *
 * Optional:
 * exact        - BOOL: used for path matching in Route
 * nav          - STRING: if in navbar, which section.  'main' or 'sub'
 * alt          - STRING: alt text for icon image
 * img          - STRING: image src string for icon button
 * featureFlag  - STRING: feature flag name if feature is to be shown/hidden based on flag
 * id           - automation id if DIFFERENT from `nav-menu-${morphed(title)}`  - morphed = lower case and spaces as dashes, remove '/'s
 * open         - BOOL: automatially open or close the account list
 * hideOnTiers  - ARRAY of string: Hide this page based on subscription tiers
 * pathParams   - ARRAY of strings: each string in the array represents a possible combination of params to add to the path
 *                  exact MUST be true (meaning # of params must be exact as well) to avoid defaulting to root path, see reports path below for example
 */
const defaultPage: FeaturePage = {
  title: '',
  exact: true,
  open: true,
  nav: 'main',
  alt: 'icon',
  img: navIcons.navIconDashboard,
  datasetNotNeeded: false,
  authNotNeeded: false,
  accountShowing: !isAcme,
  hideNav: false,
  hideOnTiers: [],
  addFIInitiator: 'app',
  pathParams: [],
};

export const featurePages: Array<FeaturePage> = [
  {
    ...defaultPage,
    alt: undefined,
    path: '/login',
    component: Login,
    exact: true,
    title: 'Login',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/logout',
    component: Logout,
    exact: true,
    title: 'Logout',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/budgets',
    component: ProtectedBudgets,
    exact: true,
    nav: 'main',
    title: 'Budgets',
    alt: 'pencil, calculator, and paper',
    img: navIcons.navIconBudgets,
    featureFlag: 'budgets',
  },
  {
    ...defaultPage,
    path: '/',
    component: ProtectedDashboard,
    exact: true,
    open: true,
    nav: 'main',
    title: 'Dashboard',
    alt: 'easel showing abstract chart',
    img: navIcons.navIconDashboard,
    qCards: qCards.overviewPage,
    showSetupBar: true,
  },
  {
    ...defaultPage,
    path: '/investments',
    component: ProtectedInvestmentHoldings,
    exact: true,
    open: true,
    nav: 'main',
    title: 'Investments',
    alt: 'bank vault with open door',
    img: navIcons.navIconInvestments,
    featureFlag: 'investmentSummaryCard',
    hideOnTiers: ['US_QUICKEN_STARTER', 'CA_QUICKEN_CASHMANAGER'],
    useSelectedAccount: true,
    preserveDisplayNode: !isAcme,
    noScroll: false,
  },
  {
    ...defaultPage,
    path: '/investing',
    component: ProtectedInvestments,
    exact: true,
    open: true,
    nav: 'main',
    title: 'Investments',
    alt: 'bank vault with open door',
    img: navIcons.navIconInvestments,
    featureFlag: 'investmentsAcme',
    noScroll: false,
  },
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///
///////////////////
////////////////////////
////////////////////////////////////
////////////////
////////////////
////////////////////////
//////////////////////
///////////////////
////
///////////
  {
    ...defaultPage,
    path: '/settings',
    component: ProtectedSettingsComponent,
    nav: 'main',
    exact: true,
    title: 'Settings',
    alt: 'settings icon',
    img: navIcons.navIconSettings,
    featureFlag: 'settings',
    pathParams: [
      "/:settingSelected",
    ],
  },

  {
    ...defaultPage,
    path: '/bills-and-income',
    component: ProtectedScheduledTransactions,
    exclusiveProject: 'quicken',
    exact: true,
    alt: 'arrow clock',
    img: navIcons.navIconRecurring,
    title: 'Bills and Income',
    featureFlag: 'scheduledTransactions',
  },
  {
    ...defaultPage,
    path: '/setup',
    component: SetupPage,
    exclusiveProject: 'quicken',
    exact: true,
    title: 'Setup',
    datasetNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/spending',
    component: ProtectedSpending,
    exact: true,
    nav: 'main',
    title: 'Spending',
    alt: 'donut chart',
    img: navIcons.navIconSpending,
    featureFlag: 'spending',
  },
  {
    ...defaultPage,
    path: '/reports',
    component: ProtectedReports,
    exact: true,
    nav: 'main',
    title: 'Reports',
    alt: 'various spending reports and charts',
    img: navIcons.navIconReports,
    featureFlag: 'reports',
    // to enable different number of params, we have to use exact=true and repeat them
    pathParams: [
      "/:levelOneTabsName",
      "/:levelOneTabsName/:levelTwoTabsName",
    ],
  },
  {
    ...defaultPage,
    path: '/net-income',
    component: ProtectedNetIncome,
    exact: true,
    nav: 'main',
    title: 'Income',
    alt: 'bar graph with positive and negative values',
    img: navIcons.navIconNetIncome,
    featureFlag: 'income',
  },
  {
    ...defaultPage,
    path: '/transactions',
    component: ProtectedTransactions,
    exact: true,
    open: true,
    nav: 'main',
    title: 'Transactions',
    alt: 'ledger of printed transactions',
    img: isAcme ? navIcons.navIconTransactions2 : navIcons.navIconTransactions,
    useSelectedAccount: true,
    preserveDisplayNode: !isAcme,
    noScroll: false,
    qCards: qCards.transactionPage,
    accountShowing: true,
    ...(isAcme && { addFIInitiator: 'account list txns' }),
  },
  {
    ...defaultPage,
    path: '/watchlist',
    component: ProtectedSpendingWatchlist,
    exclusiveProject: 'acme',
    exact: true,
    nav: 'main',
    title: 'Watchlist',
    alt: 'bar graph with values',
    img: navIcons.navIconWatchlist,
    featureFlag: 'spendingWatchList',
  },
  {
    ...defaultPage,
    path: '/terms-of-use',
    component: TermsPage,
    exclusiveProject: 'acme',
    exact: true,
    title: 'Terms of Use',
    authNotNeeded: true,
  },

  {
    ...defaultPage,
    path: '/privacy',
    component: PrivacyPage,
    exclusiveProject: 'acme',
    exact: true,
    title: 'Privacy',
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/acknowledgements',
    exclusiveProject: 'acme',
    component: Acknowledge,
    exact: true,
    title: 'Acknowledgements',
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/goals',
    component: ProtectedGoals,
    exclusiveProject: 'acme',
    exact: true,
    nav: 'main',
    title: 'Goals',
    alt: 'bar graph with values',
    img: navIcons.navIconGoals,
    featureFlag: 'goals',
  },
  {
    ...defaultPage,
    path: '/miles',
    component: ProtectedMiles,
    exclusiveProject: 'acme',
    exact: true,
    nav: 'main',
    title: 'Miles',
    alt: 'bar graph with values',
    img: navIcons.navIconBizMiles,
    featureFlag: 'bizMiles',
  },
  {
    ...defaultPage,
    path: '/spending-plan',
    component:  ProtectedSpendingPlan,
    exclusiveProject: 'acme',
    exact: true,
    nav: 'main',
    title: 'Spending Plan',
    alt: 'spending plan icon',
    img: navIcons.navIconFts,
    featureFlag: 'spendingPlan',
  },
  {
    ...defaultPage,
    path: '/upcoming',
    component:  ProtectedUpcomingPage,
    exclusiveProject: 'acme',
    exact: true,
    nav: 'main',
    title: 'Upcoming',
    alt: 'upcoming icon',
    img: navIcons.navIconUpcoming,
    featureFlag: 'upcoming',
  },
  {
    ...defaultPage,
    exact: true,
    path: '/get-started',
    exclusiveProject: 'acme',
    component: SetupPageAcme,
    title: 'Getting Started',
    datasetNotNeeded: true,
    hideNav: true,
    accountsShowing: false,
  },
  {
    ...defaultPage,
    exact: true,
    path: '/getting-started-webfirst',
    exclusiveProject: 'quicken',
    component: GettingStartedWebFirst,
    title: 'Getting Started',
    ignoreZeroState: true,
  },
  {
    ...defaultPage,
    exact: true,
    path: '/error',
    component: ErrorPage,
    title: 'Error',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    exact: true,
    path: '/oops',
    component: NotFoundPage,
    title: 'Oops',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/fetch-subscription',
    component: FetchSubscriptionPage,
    title: 'Fetch Subscription',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  {
    ...defaultPage,
    path: '/getting-started',
    component: GettingStartedPage,
    title: 'Getting Started',
    datasetNotNeeded: true,
    authNotNeeded: true,
    exclusiveProject: 'quicken',
  },
  {
    ...defaultPage,
    path: '/platformSelector',
    component: PlatformSelectorPage,
    title: 'Select a platform',
    datasetNotNeeded: true,
    exclusiveProject: 'quicken',
  },
  {
    ...defaultPage,
    exact: true,
    path: '/offline',
    component: OfflinePage,
    title: 'Offline page',
    datasetNotNeeded: false,
    authNotNeeded: true,
  },


  // UP UP UP ^^^ - insert new components above that line - NotFoundPage have to be the last one -----------------------
  {
    ...defaultPage,
    exact: true,
    path: '',
    component: NotFoundPage,
    title: 'Not Found Page',
    datasetNotNeeded: true,
    authNotNeeded: true,
  },
  // NO! NO! NO! - please don't add any components here - NotFoundPage have to be the last one -------------------------
];
